import type { ComponentProps, PropsWithChildren } from "react";
import React, { createContext } from "react";
import type { SharedValue } from "react-native-reanimated";
import Animated, {
  Easing,
  ReduceMotion,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from "react-native-reanimated";
import { styled, View } from "tamagui";

import { Button } from "../../ui/button";
import { resolveLayout, useDialogContext } from "./dialog";

interface ChatContainerContext {
  expanded: boolean;
  setExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  initialContainerState: {
    width: number | undefined;
    height: number | undefined;
  };
  container: {
    width: SharedValue<number | undefined>;
    height: SharedValue<number | undefined>;
  };
}

const ChatContainerContext = createContext<ChatContainerContext | null>(null);

const useChatUiContainerContext = () => {
  const context = React.useContext(ChatContainerContext);
  if (!context) {
    throw new Error(
      "useChatUiContainerContext must be used within a ChatContainerProvider",
    );
  }
  return context;
};

const ChatContainerProvider: React.FC<
  PropsWithChildren & {
    height?: number;
    width?: number;
  }
> = ({ children, height, width }) => {
  const [expanded, setExpanded] = React.useState(false);
  const containerHeight = useSharedValue(height);
  const containerWidth = useSharedValue(width);

  return (
    <ChatContainerContext.Provider
      value={{
        expanded,
        setExpanded,
        container: {
          width: containerWidth,
          height: containerHeight,
        },
        initialContainerState: {
          height,
          width,
        },
      }}
    >
      {children}
    </ChatContainerContext.Provider>
  );
};

type ContainerProps = ComponentProps<typeof Animated.View> & {
  height?: number;
  width?: number;
};

const Container: React.FC<ContainerProps> = ({ height, width, ...rest }) => {
  return (
    <ChatContainerProvider height={height} width={width}>
      <ContainerBody height={height} width={width} {...rest} />
    </ChatContainerProvider>
  );
};

const ContainerBody: React.FC<ContainerProps> = ({
  children,
  style,
  ...rest
}) => {
  const { container } = useChatUiContainerContext();

  const animatedLayout = useAnimatedStyle(() => ({
    width: container.width.value,
    height: container.height.value,
  }));

  return (
    <Animated.View style={[animatedLayout, style]} {...rest}>
      {children}
    </Animated.View>
  );
};

type ButtonProps = Exclude<ComponentProps<typeof Button>, "children">;

function ToggleExpandButton(props: {
  asChild: true;
  children: React.ReactElement;
}): React.JSX.Element;

function ToggleExpandButton(
  props: {
    asChild?: false;
    children: React.ReactNode;
  } & ButtonProps,
): React.JSX.Element;

function ToggleExpandButton({
  asChild = false,
  children,
  ...rest
}:
  | { asChild: true; children: React.ReactElement }
  | ({ asChild?: false; children?: React.ReactNode } & ButtonProps)) {
  const { container, setExpanded, expanded, initialContainerState } =
    useChatUiContainerContext();
  const { triggerLayout, contentLayout } = useDialogContext();

  const layout = React.useMemo(
    () => resolveLayout(contentLayout, triggerLayout),
    [contentLayout, triggerLayout],
  );

  const onPress = () => setExpanded((prev) => !prev);

  React.useEffect(() => {
    container.width.value = withTiming(
      expanded ? layout.width : initialContainerState?.width ?? 0,
      {
        duration: 500,
        easing: Easing.inOut(Easing.ease),
        reduceMotion: ReduceMotion.Never,
      },
    );

    container.height.value = withTiming(
      expanded ? layout.height : initialContainerState?.height ?? 0,
      {
        duration: 500,
        easing: Easing.inOut(Easing.ease),
        reduceMotion: ReduceMotion.Never,
      },
    );
  }, [container, expanded, initialContainerState, layout]);

  if (asChild && React.isValidElement(children)) {
    const config = {
      onPress,
    };
    return React.cloneElement(children, config);
  }

  return (
    <Button onPress={onPress} {...rest}>
      {children}
    </Button>
  );
}

const HeaderBase = styled(View, {
  name: "ChatUIHeader",
  width: "100%",
  height: "$10",
  backgroundColor: "$green-500",
});

const FooterBase = styled(View, {
  name: "ChatUIFooter",
  width: "100%",
  height: "$8.5",
  borderTopWidth: 1,
  borderTopColor: "$gray-200",
});

const Header: React.FC<
  Omit<ComponentProps<typeof HeaderBase>, "children"> & {
    children: React.ReactNode | ((expanded: boolean) => React.ReactNode);
  }
> = ({ children, ...rest }) => {
  const { expanded } = useChatUiContainerContext();
  if (typeof children === "function") {
    return <HeaderBase {...rest}>{children(expanded)}</HeaderBase>;
  }
  return <HeaderBase {...rest}>{children}</HeaderBase>;
};

const Footer: React.FC<
  Omit<ComponentProps<typeof FooterBase>, "children"> & {
    children: React.ReactNode | ((expanded: boolean) => React.ReactNode);
  }
> = ({ children, ...rest }) => {
  const { expanded } = useChatUiContainerContext();

  if (typeof children === "function") {
    return (
      <FooterBase
        {...(expanded && {
          height: 106,
        })}
        {...rest}
      >
        {children(expanded)}
      </FooterBase>
    );
  }

  return (
    <FooterBase
      {...(expanded && {
        height: 106,
      })}
      {...rest}
    >
      {children}
    </FooterBase>
  );
};

export { Container, Footer, Header, ToggleExpandButton };
